/**
 * "Amigos e chat" (docs/spec/friends.md): a coluna de amigos presa no canto de baixo à direita, por
 * cima de qualquer tela, e as janelas de conversa ao lado dela.
 *
 * - O cabeçalho fica sempre à vista: a minha foto, "Amigos", quantos estão online e as não lidas.
 *   Clicar nele abre e fecha. Com o painel aberto, clicar na foto escolhe o meu status (Online,
 *   Ocupado ou Offline); fechado, a foto é parte do botão que abre.
 * - Aberta: busca, as abas Amigos / Pedidos / Bloqueados e "Adicionar amigo". Os amigos vêm em
 *   grupos (na mesa, online, offline, as "Outras conversas" com quem não é amigo e, recolhidos,
 *   os escondidos); clicar abre a conversa; o ⋯, o botão direito ou segurar o dedo dão o menu.
 * - No celular, o painel ocupa a tela (a aba "Amigos" da barra de baixo, ou o botão no topo da
 *   mesa) e a conversa aberta toma o lugar da lista, com o voltar.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import type { ContactView, ConversationView, UserStatus } from '../shared.js';
import { Icon } from '../ui/icons.js';
import { SearchBox } from '../ui/SearchBox.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { askConfirm } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { useMobile } from '../ui/mobile.js';
import { useLongPress } from '../ui/touch.js';
import { Avatar } from './Avatar.js';
import { FriendChatWindow, friendStatusText, useNow } from './FriendChatWindow.js';
import { groupFriends, onlineCount, otherConversations, presenceDot, unreadTotal, type FriendGroupKey } from './friendsModel.js';
import { askBlock, askFriendRequest } from './dm.js';
import { useFriendsUi, type FriendsTab } from './friendsUi.js';
import { onWindowMessage } from '../room/windows.js';
import './friends.css';

/** Todos os amigos (os escondidos também) e a pessoa, achados pelo id. */
export function useFriend(userId: string): ContactView | undefined {
  return useApp((s) => s.contacts?.friends.find((c) => c.user.id === userId) ?? s.contacts?.hidden.find((c) => c.user.id === userId));
}

/**
 * `phone`: o painel em tela cheia; `inRoom`: dentro da mesa (no celular, sem a barra de baixo).
 */
export function FriendsDock({ inRoom }: { inRoom: boolean }) {
  const t = useT();
  const mobile = useMobile();
  const open = useFriendsUi((s) => s.open);
  const windows = useFriendsUi((s) => s.windows);
  const ref = useRef<HTMLElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  // os avisos (toasts) sobem para cima da coluna, em vez de cobri-la
  useLayoutEffect(() => {
    const column = columnRef.current;
    const root = document.documentElement;
    if (!column || mobile) {
      root.style.removeProperty('--rd-friends-h');
      return;
    }
    const measure = () => root.style.setProperty('--rd-friends-h', `${column.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--rd-friends-h');
    };
  }, [mobile]);

  // a conversa que uma janela destacada pediu (perfil → Conversar)
  useEffect(
    () =>
      onWindowMessage((message) => {
        if (message.t !== 'open-dm') return;
        useApp.getState().openChat(message.userId);
        window.focus();
      }),
    [],
  );

  if (mobile) {
    if (!open) return null;
    // a conversa aberta toma o lugar da lista
    const chat = [...windows].reverse().find((w) => !w.minimized);
    return (
      <aside ref={ref} className={`rd-friends rd-friends--phone${inRoom ? ' is-inRoom' : ''}`} aria-label={t('friends.title')}>
        {chat ? <FriendChatWindow userId={chat.userId} minimized={false} phone /> : <FriendsColumn columnRef={columnRef} phone />}
      </aside>
    );
  }

  return (
    <aside ref={ref} className="rd-friends" aria-label={t('friends.title')}>
      <FriendsColumn columnRef={columnRef} />
      {windows.length ? (
        <div className="rd-fchats">
          {windows.map((w) => (
            <FriendChatWindow key={w.userId} userId={w.userId} minimized={w.minimized} />
          ))}
        </div>
      ) : null}
    </aside>
  );
}

/** A coluna: cabeçalho e, aberta, a busca, as abas e a lista. */
function FriendsColumn({ columnRef, phone = false }: { columnRef: React.RefObject<HTMLDivElement | null>; phone?: boolean }) {
  const t = useT();
  const contacts = useApp((s) => s.contacts);
  const open = useFriendsUi((s) => s.open);
  const toggle = useFriendsUi((s) => s.toggle);
  const setOpen = useFriendsUi((s) => s.setOpen);
  const tab = useFriendsUi((s) => s.tab);
  const setTab = useFriendsUi((s) => s.setTab);
  const show = useFriendsUi((s) => s.show);
  const query = useFriendsUi((s) => s.query);
  const setQuery = useFriendsUi((s) => s.setQuery);
  const unread = unreadTotal(contacts);
  const incoming = contacts?.incoming.length ?? 0;
  const bodyId = 'rd-friends-body';
  const line = t('friends.summary', { online: onlineCount(contacts?.friends ?? []) });

  const tabs: { key: FriendsTab; label: string; count: number }[] = [
    { key: 'friends', label: t('friends.tab.friends'), count: 0 },
    { key: 'requests', label: t('friends.tab.requests'), count: incoming },
    { key: 'blocked', label: t('friends.tab.blocked'), count: 0 },
  ];
  const onTabKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const i = tabs.findIndex((x) => x.key === tab);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!;
    setTab(next.key);
    (e.currentTarget.querySelector(`[data-tab="${next.key}"]`) as HTMLElement | null)?.focus();
  };

  return (
    <div ref={columnRef} className={`rd-friends__col${open ? ' is-open' : ''}`}>
      {phone ? (
        <div className="rd-friends__phoneHead">
          <button type="button" className="rd-friends__icon" aria-label={t('common.close')} onClick={() => setOpen(false)}>
            <Icon name="chevronLeft" size={22} />
          </button>
          <MyStatusButton size={30} />
          <span className="rd-friends__phoneTitle">{t('friends.head')}</span>
          <span className="rd-friends__summary">{line}</span>
        </div>
      ) : (
        <div className="rd-friends__bar">
          {/* aberto, a foto escolhe o status; fechado, ela vai dentro do botão que abre o painel */}
          {open ? <MyStatusButton size={34} /> : null}
          <button type="button" className="rd-friends__head" aria-expanded={open} aria-controls={bodyId} onClick={toggle}>
            {open ? null : (
              <span className="rd-friends__me" aria-hidden="true">
                <MyPhoto size={34} />
              </span>
            )}
            <span className="rd-friends__headText">
              <span className="rd-friends__title">{t('friends.head')}</span>
              <span className="rd-friends__summary">{line}</span>
            </span>
            {!open && unread ? (
              <span className="rd-count" aria-label={t('friends.unread', { n: unread })}>
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
            <span className={`rd-friends__chevron${open ? ' is-open' : ''}`} aria-hidden="true">
              <Icon name="chevronDown" size={20} />
            </span>
          </button>
        </div>
      )}
      {open ? (
        <div id={bodyId} className="rd-friends__body">
          <SearchBox className="rd-friends__search" value={query} onChange={setQuery} placeholder={t('friends.search')} onFocus={() => tab !== 'friends' && setTab('friends')} />
          <div className="rd-friends__tabs">
            <div className="rd-friends__tabList" role="tablist" aria-label={t('friends.title')} onKeyDown={onTabKey}>
              {tabs.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  role="tab"
                  data-tab={x.key}
                  aria-selected={tab === x.key}
                  tabIndex={tab === x.key ? 0 : -1}
                  className={`rd-friends__tab${tab === x.key ? ' is-on' : ''}`}
                  onClick={() => setTab(x.key)}
                >
                  {x.label}
                  {x.count ? <span className="rd-count">{x.count}</span> : null}
                </button>
              ))}
            </div>
            <button type="button" className="rd-friends__icon" aria-label={t('friends.add')} title={t('friends.add')} onClick={() => show('requests')}>
              <Icon name="userPlus" size={19} />
            </button>
          </div>
          <div className="rd-friends__scroll" role="tabpanel">
            {tab === 'friends' ? <FriendsList /> : tab === 'requests' ? <RequestsTab /> : <BlockedTab />}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const MY_STATUSES: readonly UserStatus[] = ['online', 'busy', 'offline'];

/** A minha foto, com o ponto do meu status (sem ponto até o `hello`). */
function MyPhoto({ size }: { size: number }) {
  const me = useApp((s) => s.user);
  const myStatus = useApp((s) => s.myStatus);
  return <Avatar name={me?.displayName ?? '?'} color={me?.color ?? '#555'} userId={me?.id} avatarVersion={me?.avatarVersion ?? 0} size={size} presence={myStatus ?? undefined} />;
}

/**
 * A minha foto com o painel aberto: clicar abre o menu Online / Ocupado / Offline
 * (docs/spec/friends.md, "Status"); servidor sem status, é só a foto.
 */
function MyStatusButton({ size }: { size: number }) {
  const t = useT();
  const myStatus = useApp((s) => s.myStatus);
  const supported = useApp((s) => s.serverFeatures.status);
  const setMyStatus = useApp((s) => s.setMyStatus);
  const [menu, setMenu] = useState<MenuAnchor | null>(null);
  const avatar = <MyPhoto size={size} />;
  if (!supported) return <span className="rd-friends__me">{avatar}</span>;
  const current = myStatus ?? 'online';
  const label = t('friends.me.status', { status: t(`friends.me.${current}`) });
  const entries: MenuEntry[] = MY_STATUSES.map((status) => ({
    label: t(`friends.me.${status}`),
    hint: t(`friends.me.${status}.hint`),
    icon: <span className={`rd-avatar__mark is-${status}`} aria-hidden="true" />,
    checked: status === current,
    onSelect: () => setMyStatus(status),
  }));
  return (
    <>
      <button
        type="button"
        className="rd-friends__me"
        aria-haspopup="menu"
        aria-expanded={!!menu}
        aria-label={label}
        title={label}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMenu({ x: rect.left, y: rect.top - 6, up: true });
        }}
      >
        {avatar}
      </button>
      {menu ? <ContextMenu anchor={menu} entries={entries} label={t('friends.me.menu')} onClose={() => setMenu(null)} /> : null}
    </>
  );
}

function FriendsList() {
  const t = useT();
  const contacts = useApp((s) => s.contacts);
  const query = useFriendsUi((s) => s.query);
  const collapsed = useFriendsUi((s) => s.collapsed);
  const setGroupShut = useFriendsUi((s) => s.setGroupShut);
  const show = useFriendsUi((s) => s.show);
  const now = useNow();
  if (!contacts) return <p className="rd-friends__empty">{t('friends.loading')}</p>;
  const groups = groupFriends(contacts, query);
  const others = otherConversations(contacts.others, query);
  const q = query.trim();
  if (!groups.length && !others.length) {
    if (!q) return <p className="rd-friends__empty">{t('friends.empty')}</p>;
    return (
      <div className="rd-friends__noMatch">
        <p>{t('friends.noMatch', { q })}</p>
        <button type="button" className="rd-btn rd-btn--secondary" onClick={() => show('requests', q)}>
          {t('friends.searchPeople', { q })}
        </button>
      </div>
    );
  }
  /** Um grupo recolhível: escondidos começam recolhidos; os outros, abertos. Buscando, tudo aparece. */
  const group = (key: FriendGroupKey | 'others', count: number, rows: () => ReactNode) => {
    const shut = !q && (collapsed[key] ?? key === 'hidden');
    return (
      <section key={key} className="rd-friends__group" aria-label={t(`friends.group.${key}`)}>
        <button type="button" className="rd-friends__groupHead" aria-expanded={!shut} onClick={() => setGroupShut(key, !shut)}>
          <span className={`rd-friends__groupChevron${shut ? ' is-shut' : ''}`} aria-hidden="true">
            <Icon name="chevronDown" size={14} />
          </span>
          {t(`friends.group.${key}`)}
          <span className="rd-friends__groupCount">— {count}</span>
        </button>
        {shut ? null : rows()}
      </section>
    );
  };
  const friendGroup = (g: (typeof groups)[number]) => group(g.key, g.friends.length, () => g.friends.map((friend) => <FriendRow key={friend.user.id} friend={friend} group={g.key} now={now} />));
  // quem não é amigo vem depois do Offline e antes dos escondidos
  return (
    <>
      {groups.filter((g) => g.key !== 'hidden').map(friendGroup)}
      {others.length ? group('others', others.length, () => others.map((other) => <OtherRow key={other.user.id} other={other} />)) : null}
      {groups.filter((g) => g.key === 'hidden').map(friendGroup)}
    </>
  );
}

/** Uma conversa com quem não é amigo: sem presença; clicar abre a conversa; ⋯, botão direito ou segurar dão o menu. */
function OtherRow({ other }: { other: ConversationView }) {
  const t = useT();
  const openChat = useApp((s) => s.openChat);
  const typing = useApp((s) => !!s.dmTyping[other.user.id]);
  const isOpen = useFriendsUi((s) => s.windows.some((w) => w.userId === other.user.id && !w.minimized));
  const [menu, setMenu] = useState<MenuAnchor | null>(null);
  const press = useLongPress();
  const name = other.user.displayName;
  const unread = other.unreadCount;
  const status = typing ? { text: t('friends.status.typing'), tone: 'typing' } : { text: t('friends.status.notFriend'), tone: 'offline' };
  return (
    <div className={`rd-friends__row${isOpen ? ' is-open' : ''}`} {...press.bind(setMenu)}>
      <button
        type="button"
        className="rd-friends__rowMain"
        aria-label={[name, status.text, unread ? t('friends.unread', { n: unread }) : ''].filter(Boolean).join(', ')}
        onClick={() => openChat(other.user.id)}
      >
        <span className="rd-friends__rowAvatar">
          <Avatar name={name} color={other.user.color} userId={other.user.id} avatarVersion={other.user.avatarVersion} size={36} />
        </span>
        <span className="rd-friends__rowText">
          <span className="rd-friends__name">{name}</span>
          <span className={`rd-friends__status is-${status.tone}`}>{status.text}</span>
        </span>
        {unread ? <span className="rd-count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      <button type="button" className="rd-friends__more" aria-label={t('friends.more', { name })} onClick={(e) => setMenu({ x: e.clientX, y: e.clientY })}>
        ⋯
      </button>
      {menu ? <OtherMenu other={other} anchor={menu} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}

/** O menu de uma conversa com quem não é amigo. */
function OtherMenu({ other, anchor, onClose }: { other: ConversationView; anchor: MenuAnchor; onClose: () => void }) {
  const t = useT();
  const openChat = useApp((s) => s.openChat);
  const viewProfile = useApp((s) => s.viewProfile);
  const dismiss = useApp((s) => s.dismissConversation);
  const pending = useApp((s) => !!s.contacts?.incoming.some((c) => c.user.id === other.user.id) || !!s.contacts?.outgoing.some((c) => c.user.id === other.user.id));
  const id = other.user.id;
  const name = other.user.displayName;
  const entries: MenuEntry[] = [
    { kind: 'title', label: name },
    { label: t('friends.menu.chat'), onSelect: () => openChat(id) },
    { label: t('friends.menu.profile'), onSelect: () => void viewProfile(id) },
  ];
  if (!pending) entries.push({ label: t('friends.menu.add'), onSelect: () => void askFriendRequest(other.user, t) });
  entries.push({ kind: 'separator' });
  entries.push({ label: t('friends.menu.dismiss'), onSelect: () => void dismiss(id) });
  entries.push({ label: t('friends.menu.block'), danger: true, onSelect: () => void askBlock(other.user, t) });
  return <ContextMenu anchor={anchor} entries={entries} label={t('friends.more', { name })} onClose={onClose} />;
}

/** Uma linha de amigo: clicar abre a conversa; ⋯, botão direito ou segurar dão o menu. */
function FriendRow({ friend, group, now }: { friend: ContactView; group: FriendGroupKey; now: number }) {
  const t = useT();
  const openChat = useApp((s) => s.openChat);
  const typing = useApp((s) => !!s.dmTyping[friend.user.id]);
  const isOpen = useFriendsUi((s) => s.windows.some((w) => w.userId === friend.user.id && !w.minimized));
  const [menu, setMenu] = useState<MenuAnchor | null>(null);
  const press = useLongPress();
  const status = friendStatusText(friend, typing, now, t);
  const name = friend.user.displayName;
  const unread = friend.unreadCount;
  const dot = presenceDot(friend.presence);
  return (
    <div className={`rd-friends__row${isOpen ? ' is-open' : ''}${dot === 'offline' ? ' is-offline' : ''}`} {...press.bind(setMenu)}>
      <button
        type="button"
        className="rd-friends__rowMain"
        aria-label={[name, status.text, unread ? t('friends.unread', { n: unread }) : ''].filter(Boolean).join(', ')}
        onClick={() => openChat(friend.user.id)}
      >
        <span className="rd-friends__rowAvatar">
          <Avatar name={name} color={friend.user.color} userId={friend.user.id} avatarVersion={friend.user.avatarVersion} size={36} presence={dot} />
        </span>
        <span className="rd-friends__rowText">
          <span className="rd-friends__name">{name}</span>
          <span className={`rd-friends__status is-${status.tone}`}>{status.text}</span>
        </span>
        {unread ? <span className="rd-count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      <button type="button" className="rd-friends__more" aria-label={t('friends.more', { name })} onClick={(e) => setMenu({ x: e.clientX, y: e.clientY })}>
        ⋯
      </button>
      {menu ? <FriendMenu friend={friend} hidden={group === 'hidden'} anchor={menu} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}

/** O menu de um amigo. */
export function FriendMenu({ friend, hidden, anchor, onClose }: { friend: ContactView; hidden: boolean; anchor: MenuAnchor; onClose: () => void }) {
  const t = useT();
  const openChat = useApp((s) => s.openChat);
  const viewProfile = useApp((s) => s.viewProfile);
  const contactAction = useApp((s) => s.contactAction);
  const inviteFriends = useApp((s) => s.inviteFriends);
  const activeRoomId = useApp((s) => s.activeRoomId);
  const canInvite = useApp((s) => s.serverFeatures.presence && !!s.room && s.room.id === s.activeRoomId);
  const closeWindow = useFriendsUi((s) => s.closeWindow);
  const id = friend.user.id;
  const name = friend.user.displayName;
  const entries: MenuEntry[] = [
    { kind: 'title', label: name },
    { label: t('friends.menu.chat'), onSelect: () => openChat(id) },
    { label: t('friends.menu.profile'), onSelect: () => void viewProfile(id) },
  ];
  if (canInvite && activeRoomId && friend.presence.room?.id !== activeRoomId)
    entries.push({
      label: t('friends.menu.invite'),
      onSelect: async () => {
        if (await inviteFriends([id])) toast(t('friends.invite.done', { n: 1 }), 'success');
      },
    });
  entries.push({ kind: 'separator' });
  entries.push(hidden ? { label: t('friends.menu.unhide'), onSelect: () => void contactAction(id, 'unhide') } : { label: t('friends.menu.hide'), onSelect: () => void contactAction(id, 'hide') });
  entries.push({
    label: t('friends.menu.remove'),
    danger: true,
    onSelect: async () => {
      if (!(await askConfirm({ title: t('friends.menu.remove'), message: t('friends.remove.confirm', { name }), action: t('friends.menu.remove'), danger: true }))) return;
      closeWindow(id);
      await contactAction(id, 'reject');
    },
  });
  entries.push({
    label: t('friends.menu.block'),
    danger: true,
    onSelect: async () => {
      if (!(await askConfirm({ title: t('friends.menu.block'), message: t('friends.block.confirm', { name }), action: t('friends.menu.block'), danger: true }))) return;
      closeWindow(id);
      await contactAction(id, 'block');
    },
  });
  return <ContextMenu anchor={anchor} entries={entries} label={t('friends.more', { name })} onClose={onClose} />;
}

/** Pedidos: adicionar pelo nome, os enviados e os recebidos. */
function RequestsTab() {
  const t = useT();
  const contacts = useApp((s) => s.contacts);
  const requestContact = useApp((s) => s.requestContact);
  const contactAction = useApp((s) => s.contactAction);
  const searchUsers = useApp((s) => s.searchUsers);
  const suggestions = useApp((s) => s.userSearch);
  const me = useApp((s) => s.user?.id);
  const addName = useFriendsUi((s) => s.addName);
  const setAddName = useFriendsUi((s) => s.setAddName);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // sugestões de nomes enquanto digita (a partir de 2 letras)
  useEffect(() => {
    const q = addName.trim();
    const timer = window.setTimeout(() => void searchUsers(q.length >= 2 ? q : ''), 300);
    return () => window.clearTimeout(timer);
  }, [addName, searchUsers]);
  // vindo de "Procurar no RPG Desk" (ou do botão de adicionar): o campo já com o foco
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const name = addName.trim();
    if (!name || busy) return;
    setBusy(true);
    const ok = await requestContact({ displayName: name }, null);
    setBusy(false);
    if (ok) {
      toast(t('friends.request.sent', { name }), 'success');
      setAddName('');
    }
  };
  const shown = suggestions.filter((s) => s.user.id !== me && s.relation === null && s.user.displayName.toLowerCase() !== addName.trim().toLowerCase()).slice(0, 5);
  const incoming = contacts?.incoming ?? [];
  const outgoing = contacts?.outgoing ?? [];

  return (
    <div className="rd-friends__requests">
      <div className="rd-friends__section">
        <label htmlFor="rd-friends-add" className="rd-friends__sectionTitle">
          {t('friends.add')}
        </label>
        <div className="rd-friends__addRow">
          <input
            id="rd-friends-add"
            ref={inputRef}
            className="rd-friends__input"
            type="text"
            maxLength={40}
            autoComplete="off"
            placeholder={t('friends.add.placeholder')}
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button type="button" className="rd-btn rd-btn--action" disabled={!addName.trim() || busy} onClick={() => void send()}>
            {t('friends.add.send')}
          </button>
        </div>
        {shown.length ? (
          <ul className="rd-friends__suggest" aria-label={t('friends.add.suggestions')}>
            {shown.map((s) => (
              <li key={s.user.id}>
                <button type="button" onClick={() => setAddName(s.user.displayName)}>
                  <Avatar name={s.user.displayName} color={s.user.color} userId={s.user.id} avatarVersion={s.user.avatarVersion} size={24} />
                  {s.user.displayName}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {outgoing.length ? (
        <section className="rd-friends__section" aria-label={t('friends.requests.sent', { n: outgoing.length })}>
          <h3 className="rd-friends__sectionTitle">{t('friends.requests.sent', { n: outgoing.length })}</h3>
          {outgoing.map((c) => (
            <div key={c.user.id} className="rd-friends__request">
              <Avatar name={c.user.displayName} color={c.user.color} userId={c.user.id} avatarVersion={c.user.avatarVersion} size={36} />
              <span className="rd-friends__rowText">
                <span className="rd-friends__name">{c.user.displayName}</span>
                <span className="rd-friends__status">{t('friends.requests.waiting')}</span>
              </span>
              <button type="button" className="rd-btn rd-btn--secondary rd-friends__small" onClick={() => void contactAction(c.user.id, 'reject')}>
                {t('friends.requests.cancel')}
              </button>
            </div>
          ))}
        </section>
      ) : null}

      <section className="rd-friends__section" aria-label={t('friends.requests.received', { n: incoming.length })}>
        <h3 className="rd-friends__sectionTitle">{t('friends.requests.received', { n: incoming.length })}</h3>
        {incoming.length ? (
          incoming.map((c) => (
            <div key={c.user.id} className="rd-friends__request">
              <Avatar name={c.user.displayName} color={c.user.color} userId={c.user.id} avatarVersion={c.user.avatarVersion} size={36} />
              <span className="rd-friends__rowText">
                <span className="rd-friends__name">{c.user.displayName}</span>
                {c.requestMessage ? <span className="rd-friends__status rd-friends__note">{c.requestMessage}</span> : <span className="rd-friends__status">{t('friends.requests.wants')}</span>}
              </span>
              <button type="button" className="rd-friends__accept" aria-label={t('friends.requests.accept', { name: c.user.displayName })} title={t('friends.requests.accept', { name: c.user.displayName })} onClick={() => void contactAction(c.user.id, 'accept')}>
                <Icon name="check" size={18} />
              </button>
              <button type="button" className="rd-friends__decline" aria-label={t('friends.requests.decline', { name: c.user.displayName })} title={t('friends.requests.decline', { name: c.user.displayName })} onClick={() => void contactAction(c.user.id, 'reject')}>
                <Icon name="close" size={16} />
              </button>
            </div>
          ))
        ) : (
          <p className="rd-friends__empty rd-friends__empty--left">{t('friends.requests.none')}</p>
        )}
      </section>
    </div>
  );
}

/** Bloqueados, com o desbloquear. */
function BlockedTab() {
  const t = useT();
  const blocked = useApp((s) => s.contacts?.blocked ?? []);
  const contactAction = useApp((s) => s.contactAction);
  if (!blocked.length)
    return (
      <div className="rd-friends__blockedEmpty">
        <Icon name="lock" size={28} />
        <strong>{t('friends.blocked.none')}</strong>
        <p>{t('friends.blocked.hint')}</p>
      </div>
    );
  return (
    <div className="rd-friends__section">
      <p className="rd-friends__hint">{t('friends.blocked.hint')}</p>
      {blocked.map((c) => (
        <div key={c.user.id} className="rd-friends__request">
          <Avatar name={c.user.displayName} color={c.user.color} userId={c.user.id} avatarVersion={c.user.avatarVersion} size={36} />
          <span className="rd-friends__rowText">
            <span className="rd-friends__name">{c.user.displayName}</span>
          </span>
          <button type="button" className="rd-btn rd-btn--secondary rd-friends__small" onClick={() => void contactAction(c.user.id, 'unblock')}>
            {t('friends.blocked.unblock')}
          </button>
        </div>
      ))}
    </div>
  );
}
